import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { api } from '../App';
import { MessageCircle, Send, TrendingUp, LogOut, BookOpen, Sparkles } from 'lucide-react';

export default function CoachPage({ user, logout }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [coachInfo, setCoachInfo] = useState(null);
  const [progress, setProgress] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchCoachInfo();
    fetchHistory();
    fetchProgress();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchCoachInfo = async () => {
    try {
      const response = await api.get('/coach/info');
      setCoachInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch coach info:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/coach/history');
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Geçmiş yüklenirken hata oluştu');
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await api.get('/coach/progress');
      setProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    const userMessage = newMessage;
    setNewMessage('');
    setLoading(true);

    try {
      const response = await api.post('/coach/message', {
        message: userMessage
      });

      // Add messages to state
      setMessages([...messages, {
        user_message: userMessage,
        coach_reply: response.data.coach_reply,
        created_at: response.data.timestamp,
        coach_name: coachInfo?.coach?.name
      }]);

      // Update progress
      fetchProgress();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Mesaj gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const getCoachIcon = (temperamentType) => {
    const icons = {
      1: '📋', 2: '❤️', 3: '⚡', 4: '🎨', 
      5: '📚', 6: '🛡️', 7: '✨', 8: '💪', 9: '☮️'
    };
    return icons[temperamentType] || '🌟';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
              <p className="text-sm text-slate-600">Mizaç Koçu</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              ← Panele Dön
            </Button>
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Coach Info Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Coach Card */}
            {coachInfo && (
              <Card className="shadow-lg border-none">
                <CardHeader className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-full flex items-center justify-center text-4xl mx-auto mb-4">
                    {getCoachIcon(coachInfo.temperament_type)}
                  </div>
                  <CardTitle className="text-2xl">{coachInfo.coach.name}</CardTitle>
                  <CardDescription className="text-base">{coachInfo.coach.title}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 text-center">
                    {coachInfo.coach.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Progress Card */}
            {progress && (
              <Card className="shadow-lg border-none">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    İlerlemeniz
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600">Toplam Oturum</p>
                    <p className="text-2xl font-bold text-slate-800">{progress.total_sessions}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Toplam Mesaj</p>
                    <p className="text-2xl font-bold text-slate-800">{progress.total_messages}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Son 7 Gün</p>
                    <p className="text-2xl font-bold text-emerald-600">{progress.messages_last_week}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tips Card */}
            <Card className="shadow-lg border-none bg-gradient-to-br from-blue-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  İpuçları
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• Koçunuzla düzenli görüşün</li>
                  <li>• Hedeflerinizi net belirtin</li>
                  <li>• Açık ve dürüst olun</li>
                  <li>• Küçük adımlarla ilerleyin</li>
                  <li>• İlerlemenizi takip edin</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl border-none h-[calc(100vh-12rem)]">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                  Koçunuzla Konuşun
                </CardTitle>
                <CardDescription>
                  {coachInfo ? `${coachInfo.coach.name} sizinle konuşmak için hazır` : 'Yükleniyor...'}
                </CardDescription>
              </CardHeader>

              <CardContent className="p-0 flex flex-col h-[calc(100%-5rem)]">
                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                        {coachInfo && getCoachIcon(coachInfo.temperament_type)}
                      </div>
                      <p className="text-slate-600 mb-2">
                        Merhaba! Ben {coachInfo?.coach.name}, senin kişisel mizaç koçunum.
                      </p>
                      <p className="text-slate-500 text-sm">
                        Benimle hedeflerinden, zorluklarından veya mizaç özelliklerinden bahsedebilirsin.
                      </p>
                    </div>
                  ) : (
                    messages.map((msg, index) => (
                      <div key={index} className="space-y-3">
                        {/* User Message */}
                        <div className="flex justify-end">
                          <div className="bg-blue-600 text-white px-4 py-3 rounded-2xl rounded-tr-sm max-w-[80%]">
                            <p className="text-sm">{msg.user_message}</p>
                          </div>
                        </div>

                        {/* Coach Reply */}
                        <div className="flex justify-start">
                          <div className="bg-slate-100 text-slate-800 px-4 py-3 rounded-2xl rounded-tl-sm max-w-[80%]">
                            <p className="text-xs font-semibold text-emerald-600 mb-1">
                              {msg.coach_name || coachInfo?.coach.name}
                            </p>
                            <p className="text-sm whitespace-pre-wrap">{msg.coach_reply}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 text-slate-800 px-4 py-3 rounded-2xl rounded-tl-sm">
                        <div className="flex gap-2">
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t p-4">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Mesajınızı yazın..."
                      disabled={loading}
                      className="flex-1"
                    />
                    <Button 
                      type="submit" 
                      disabled={loading || !newMessage.trim()}
                      className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
